// src/data/worksheets/confidence.js — performance-side pair (authored).
// Built off the Confidence action plan: keep a wins list, prepare one thing properly, watch the self-talk.

export const CONFIDENCE_CHECKLIST = {
    id: 'confidence_checklist', topicId: 'confidence', kind: 'checklist',
    title: 'Confidence Checklist',
    intro: 'Confidence follows evidence. The plan is to collect some — tick these off as you go.',
    sections: [
        { id: 'evidence', title: 'Collect Evidence', prompts: [
            { id: 'wins', type: 'checklist', label: 'Keep a Wins List', items: [
                { id: 'w1', label: 'Each day, note one thing you did well, however small.' },
                { id: 'w2', label: 'Read the list before a key moment, not after.' }
            ] },
            { id: 'proof', type: 'checklist', label: 'Bank the Proof', items: [
                { id: 'p1', label: 'Write down three things you have already done that were hard.' },
                { id: 'p2', label: 'Ask one person what they see you do well — and write it down without arguing.' }
            ] }
        ] },
        { id: 'prep', title: 'Prepare Properly', prompts: [
            { id: 'onething', type: 'checklist', label: 'One Thing, Fully', items: [
                { id: 'o1', label: 'Pick one specific moment coming up and prepare for it completely.', detail: 'Being ready for a specific moment builds confidence faster than general positivity.' },
                { id: 'o2', label: 'Rehearse it — physically or in your head — until the first step feels automatic.' }
            ] },
            { id: 'routine', type: 'checklist', label: 'Own Your Routine', items: [
                { id: 'r1', label: 'Build a short pre-performance routine you can run anywhere.' },
                { id: 'r2', label: '{Run it in training so it belongs to you|Run it on normal days so it belongs to you} when the pressure is on.' }
            ] }
        ] },
        { id: 'selftalk', title: 'Watch the Self-Talk', prompts: [
            { id: 'catch', type: 'checklist', label: 'Catch It', items: [
                { id: 'c1', label: 'Notice when you say something to yourself you would never say to someone else.' },
                { id: 'c2', label: 'Reword it to what you would actually say to them.' }
            ] },
            { id: 'body', type: 'checklist', label: 'Body First', items: [
                { id: 'b1', label: 'Stand and breathe like someone who belongs — the body tells the mind.' },
                { id: 'b2', label: 'Slow your first action down; rushing reads as doubt, to you most of all.' }
            ] }
        ] },
        { id: 'pressure', title: 'Confidence Under Pressure', prompts: [
            { id: 'mistakes', type: 'checklist', label: 'Mistakes Are Data', items: [
                { id: 'm1', label: 'After a mistake, name the fix, not the flaw ("earlier setup", not "I\'m hopeless").' },
                { id: 'm2', label: 'Give yourself the same next-play rule you would give a {teammate|colleague}.' }
            ] },
            { id: 'comparison', type: 'checklist', label: 'Compare Forward', items: [
                { id: 'cp1', label: 'Measure against your own last month, not someone else\'s highlight reel.' },
                { id: 'cp2', label: 'Limit the scroll before key moments — comparison is a confidence leak.' }
            ] }
        ] },
        { id: 'review', title: 'Review & Reflect', prompts: [
            { id: 'weekly', type: 'checklist', label: 'Weekly', items: [
                { id: 'wk1', label: 'Read the wins list. Add the best one to your proof bank.' },
                { id: 'wk2', label: 'Note one self-talk line you caught and reworded.' }
            ] },
            { id: 'notes', type: 'text', label: 'Notes', multiline: true }
        ] }
    ],
    outro: 'Confidence is not believing you will win. It is knowing you have prepared and trusting the next action.'
};

export const CONFIDENCE_WORKSHEET = {
    id: 'confidence_worksheet', topicId: 'confidence', kind: 'worksheet',
    title: 'Confidence Worksheet',
    intro: 'Answer in your own words. Nothing here is scored, and only you can see it.',
    sections: [
        { id: 'self', title: 'Self Assessment', prompts: [
            { id: 'level', type: 'scale', label: 'How confident have you felt over the past week?', min: 1, max: 10, minLabel: 'Doubting everything', maxLabel: 'Trusting myself' },
            { id: 'situations', type: 'list', label: 'Where does your confidence drop?', help: 'Specific moments: {a certain opponent, presenting|a certain colleague, presenting}, the first minute, after a mistake.', count: 3 },
            { id: 'selftalk', type: 'list', label: 'What you say to yourself in those moments', help: 'Write the actual words.', count: 3 }
        ] },
        { id: 'understanding', title: 'Understanding Your Confidence', prompts: [
            { id: 'best', type: 'text', label: 'A moment you felt fully confident — what was behind it?', help: 'Preparation, form, people, mindset. This is your template.', multiline: true },
            { id: 'proof', type: 'list', label: 'Proof bank', help: 'Three hard things you have already done.', count: 3 },
            { id: 'others', type: 'text', label: 'What one person says you do well', help: 'Ask them. Write it without arguing.' }
        ] },
        { id: 'building', title: 'Building Confidence', prompts: [
            { id: 'rewrites', type: 'list', label: 'Self-talk rewrites', help: 'For each line above: what you would say to someone else in that spot.', count: 3 },
            { id: 'prepare', type: 'text', label: 'The one moment you will prepare for completely this week', help: 'What it is, and what "fully prepared" looks like.', multiline: true },
            { id: 'routine', type: 'text', label: 'Your pre-performance routine', help: 'Short enough to run anywhere.', multiline: true },
            { id: 'nextplay', type: 'text', label: 'Your next-play rule after a mistake', help: 'e.g., "name the fix, breathe, first action".' }
        ] },
        { id: 'plan', title: 'Action Plan', prompts: [
            { id: 'goal', type: 'text', label: 'This week\'s confidence goal' },
            { id: 'wins', type: 'list', label: 'Wins list — start it now', help: 'Three things from this week already.', count: 3 },
            { id: 'support', type: 'list', label: 'Who will you tell about the moment you\'re preparing for?', count: 2 }
        ] },
        { id: 'track', title: 'Track It', description: 'One line a day. Confidence tends to rise with the wins list, not with the results.', prompts: [
            { id: 'tracker', type: 'table', label: 'Daily confidence tracker', columns: [
                { id: 'day', label: 'Day', kind: 'date' },
                { id: 'confidence', label: 'Confidence (1–10)', kind: 'number', min: 1, max: 10 },
                { id: 'win', label: 'Win of the day', kind: 'text' },
                { id: 'caught', label: 'Self-talk caught & reworded', kind: 'text' },
                { id: 'notes', label: 'Notes', kind: 'text' }
            ], maxRows: 31, chart: { x: 'day', series: [{ id: 'confidence', label: 'Confidence' }] } }
        ] }
    ],
    outro: 'Evidence beats affirmation. Keep collecting it.'
};

export const CONFIDENCE_WORKSHEETS = [CONFIDENCE_CHECKLIST, CONFIDENCE_WORKSHEET];
